import { toast } from 'react-hot-toast';

export interface ErrorDetails {
  code?: string;
  message: string;
  details?: any;
  context?: Record<string, any>;
  timestamp: string;
  handled: boolean;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private errors: ErrorDetails[] = [];
  private errorListeners: ((error: ErrorDetails) => void)[] = [];

  private constructor() {
    this.setupGlobalHandlers();
  }

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  private setupGlobalHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        message: event.reason?.message || 'Unhandled Promise Rejection',
        details: event.reason,
        handled: false,
      });
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.logError({
        message: event.message,
        details: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        },
        handled: false,
      });
    });
  }

  public logError(error: Partial<ErrorDetails>) {
    const errorDetails: ErrorDetails = {
      message: error.message || 'An unknown error occurred',
      code: error.code,
      details: error.details,
      context: error.context,
      timestamp: new Date().toISOString(),
      handled: error.handled || false,
    };

    this.errors.push(errorDetails);
    this.notifyListeners(errorDetails);
    this.showErrorToast(errorDetails);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error logged:', errorDetails);
    }
  }

  private showErrorToast(error: ErrorDetails) {
    const message = error.code 
      ? `Error ${error.code}: ${error.message}`
      : error.message;

    toast.error(message, {
      duration: 5000,
      position: 'top-right',
    });
  }

  public addErrorListener(listener: (error: ErrorDetails) => void) {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(error: ErrorDetails) {
    this.errorListeners.forEach(listener => listener(error));
  }

  public getErrors(): ErrorDetails[] {
    return [...this.errors];
  }

  public clearErrors() {
    this.errors = [];
  }
}

export const errorLogger = ErrorLogger.getInstance();

export function handleError(error: any, context?: Record<string, any>) {
  let errorDetails: Partial<ErrorDetails>;

  if (error instanceof Error) {
    errorDetails = {
      message: error.message,
      details: error.stack,
      context,
      handled: true,
    };
  } else if (typeof error === 'string') {
    errorDetails = {
      message: error,
      context,
      handled: true,
    };
  } else {
    errorDetails = {
      message: 'An unknown error occurred',
      details: error,
      context,
      handled: true,
    };
  }

  errorLogger.logError(errorDetails);
}